/* ============================================================================
   Krumb Bakery — Cake enquiry builder
   ----------------------------------------------------------------------------
   PROGRESSIVE ENHANCEMENT ONLY. sections/cake-enquiry.liquid renders a complete,
   working form on its own: the fork question and BOTH of its paths on screen at
   once, every question visible, `required` on the two fields that matter, `min`
   on the date, and the opening-day rules written out in help text. The two path
   questions share one `contact[Cake size]` radio name, so even with no script
   at all answering one path clears the other and exactly one size is posted.

   This file only ever adds to that:
     1. shows only the path the customer picked, and collapses the rest of the
        form until a size is chosen
     2. sums servings and price across the tier stack into a live "from" figure
     3. blocks closed days and too-soon dates in the date field
     4. replaces the browser's validation bubbles with an accessible summary

   THE ESTIMATE IS DELIBERATELY JS-ONLY. With no script there is no running
   total anywhere on the page — only each size's own "from" price beside it — so
   a total that could be wrong is never shown. If a contributing size has no
   price set, the price line is suppressed entirely rather than under-counted.

   Registration uses the `customElements.get` guard because the script is loaded
   from a section: without it, re-rendering the section in the theme editor
   throws NotSupportedError. See ORIENTATION.md, "Load order is load-bearing".
   ============================================================================ */

if (!customElements.get('krumb-cake-enquiry')) {
  class KrumbCakeEnquiry extends HTMLElement {
    connectedCallback() {
      if (this.krumbReady) return;
      this.krumbReady = true;

      this.form = this.closest('form');
      if (!this.form) return;

      this.messages = {
        summaryHeading: this.dataset.errorSummaryHeading || 'Please check the following:',
        required: this.dataset.errorRequired || 'Please fill this in.',
        email: this.dataset.errorEmail || 'Please check your email address.',
        url: this.dataset.errorUrl || 'That does not look like a full web address.',
        dateSoon: this.dataset.errorDateSoon || 'That date is too soon, sorry.',
        dateClosed: this.dataset.errorDateClosed || 'We are closed that day.'
      };

      this.pricePrefix = this.dataset.pricePrefix || '';
      this.servingsTemplate = this.dataset.servingsTemplate || 'Serves roughly {min}-{max}.';
      this.servingsOpenTemplate = this.dataset.servingsOpenTemplate || 'Serves roughly {min} or more.';
      this.progressTemplate = this.dataset.progressTemplate || '{current} of {total} answered';
      this.summaryPriceTemplate = this.dataset.summaryPriceTemplate || 'Estimated from {price}';
      this.summaryCakeTemplate = this.dataset.summaryCakeTemplate || '{sizes} - {tiers}';
      this.summarySeparator = this.dataset.summarySeparator || ' / ';
      this.flavourMode = this.dataset.flavourPricingMode === 'per_tier' ? 'per_tier' : 'once';

      this.leadDays = parseInt(this.dataset.leadDays, 10);
      if (isNaN(this.leadDays) || this.leadDays < 0) this.leadDays = 14;

      this.closedDays = (this.dataset.closedDays || '')
        .split(',')
        .map(function (value) {
          return parseInt(value, 10);
        })
        .filter(function (value) {
          return !isNaN(value) && value >= 0 && value <= 6;
        });
      // Every day closed would make the date field unanswerable, so fall back to
      // "no closed days" and let the merchant's own help text stand.
      if (this.closedDays.length > 6) this.closedDays = [];

      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-krumb-cake-step]'));
      this.controlled = Array.prototype.slice.call(
        this.querySelectorAll('[data-krumb-cake-path], [data-krumb-cake-gated-by]')
      );
      this.tierRows = Array.prototype.slice.call(this.querySelectorAll('[data-krumb-cake-tier-row]'));
      this.tiersStep = this.querySelector('[data-krumb-cake-step="tiers"]');
      this.tierStack = this.querySelector('[data-krumb-cake-stack]');
      this.tierBaseSelect = this.querySelector('[data-krumb-cake-tier-base]');
      this.syncing = false;

      this.summary = this.querySelector('[data-krumb-cake-summary]');
      this.progress = this.querySelector('[data-krumb-cake-progress]');
      this.revealAllButton = this.querySelector('[data-krumb-cake-revealall]');
      this.dateInput = this.querySelector('[data-krumb-cake-date]');

      this.bar = this.querySelector('[data-krumb-cake-bar]');
      this.progressFill = this.querySelector('[data-krumb-cake-progress-fill]');
      this.barPrice = this.querySelector('[data-krumb-cake-bar-price]');
      this.estimateField = this.querySelector('[data-krumb-cake-estimate-field]');
      this.panelNote = this.querySelector('[data-krumb-cake-panel-note]');
      this.emptyNote = this.querySelector('[data-krumb-cake-empty]');
      this.actionsNote = this.querySelector('[data-krumb-cake-actions-note]');

      // A server-side error means the customer has already been all the way
      // through once. Liquid cannot repopulate their radio choices, so show the
      // whole form rather than guessing which path they were on.
      const hasServerError = !!this.querySelector('.krumb-cake-alert');
      this.gatingActive = this.controlled.length > 0 && !hasServerError;
      this.pathFilterActive = !hasServerError;

      this.setUpDateField();

      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onPageShow = this.refresh.bind(this);

      this.addEventListener('change', this.onChange);
      this.wireExtraSteppers();
      this.setUpPanelDock();
      this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('pageshow', this.onPageShow);

      if (this.revealAllButton) {
        this.revealAllButton.addEventListener('click', this.revealEverything.bind(this));
        if (this.gatingActive || this.pathFilterActive) this.revealAllButton.hidden = false;
      }

      // Own the validation UI only once everything above has wired up cleanly.
      this.form.noValidate = true;
      // With a bridge endpoint, Send is a plain button: no submit event is
      // raised on the happy path, so Shopify's capture-phase captcha
      // bootstrap never launches its puzzle. The fallback raises a real
      // submit, which the captcha then guards as before.
      if (this.dataset.enquiryEndpoint) {
        const sendBtn = this.form.querySelector('.krumb-cake-submit');
        if (sendBtn) {
          sendBtn.type = 'button';
          sendBtn.addEventListener('click', this.submitViaBridge.bind(this));
        }
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    refresh() {
      // Order matters. applyVisibility() restores `required` wholesale on a step
      // it reopens, so updateTierStack() has to run after it to re-park the tier
      // rows the customer is not using. The cascade then needs the stack to have
      // settled before it can filter, and the estimate needs the cascade to have
      // cleared anything impossible before it totals anything up.
      this.applyVisibility();
      this.syncBaseFromRadio();
      this.updateTierStack();
      this.applyTierCascade();
      this.updateSummary();
      this.renumberBadges();
      this.updateProgress();
    }

    /* ------------------------------------------------- tier 1 <-> size Q -- */

    // Tier 1 of the stack and the size question are two views of one answer.
    // The radio group is the source of truth — it is the thing with a `name`
    // and the thing that gets posted — and the select is a mirror of it.
    //
    // On loops: assigning `.value` or `.checked` from script does not fire a
    // `change` event, so these two cannot actually ping-pong. The `syncing`
    // flag is belt and braces against anything that ever does dispatch one, and
    // it makes the one-way-at-a-time intent obvious to the next reader. Each
    // user change results in exactly one sync followed by exactly one refresh.
    syncBaseFromRadio() {
      if (!this.tierBaseSelect || this.syncing) return;
      const radio = this.querySelector('input[data-krumb-cake-size]:checked');
      const value = radio ? radio.value : '';
      if (this.tierBaseSelect.value === value) return;
      this.syncing = true;
      this.tierBaseSelect.value = value;
      this.syncing = false;
    }

    syncRadioFromBase() {
      if (!this.tierBaseSelect || this.syncing) return;
      const value = this.tierBaseSelect.value;
      const radios = this.querySelectorAll('input[data-krumb-cake-size]');
      let target = null;

      for (let i = 0; i < radios.length; i++) {
        if (radios[i].value !== value) continue;
        // Both paths render the same sizes under the same `contact[Cake size]`
        // name, so prefer the copy on the path the customer is actually looking
        // at — otherwise the change lands on a question they cannot see.
        if (!radios[i].closest('[hidden]')) {
          target = radios[i];
          break;
        }
        if (!target) target = radios[i];
      }

      this.syncing = true;
      if (target) {
        // Shared name means checking one unchecks the other for free.
        target.checked = true;
      } else if (!value) {
        for (let i = 0; i < radios.length; i++) radios[i].checked = false;
      }
      this.syncing = false;
    }

    /* ------------------------------------------------------ paths + gates -- */

    currentPath() {
      const chosen = this.querySelector('input[data-krumb-cake-path-input]:checked');
      return chosen ? chosen.getAttribute('data-krumb-cake-path-input') : '';
    }

    applyVisibility() {
      const path = this.currentPath();
      const self = this;

      this.controlled.forEach(function (element) {
        // Path membership is re-evaluated every time: switching the fork has to
        // be able to put a question away again, unlike a gate.
        const pathAttr = element.getAttribute('data-krumb-cake-path');
        let pathOk = true;
        if (pathAttr && self.pathFilterActive) {
          pathOk = path !== '' && pathAttr.split(' ').indexOf(path) !== -1;
        }

        // Gates are sticky: once a question has opened it never closes again,
        // so changing an earlier answer cannot yank the page around.
        const gateAttr = element.getAttribute('data-krumb-cake-gated-by');
        let gateOk = true;
        if (gateAttr && self.gatingActive) {
          if (element.dataset.krumbCakeGateOpen === 'true') {
            gateOk = true;
          } else {
            gateOk = self.anyAnswered(gateAttr.split(' '));
            if (gateOk) element.dataset.krumbCakeGateOpen = 'true';
          }
        }

        if (pathOk && gateOk) self.reveal(element);
        else self.collapse(element);
      });
    }

    collapse(element) {
      if (element.dataset.krumbCakeShown === 'false') return;
      const wasShown = element.dataset.krumbCakeShown === 'true';
      element.dataset.krumbCakeShown = 'false';
      element.hidden = true;

      // A `required` control inside a display:none subtree makes the browser
      // report "an invalid form control is not focusable" and silently refuse to
      // submit, so requirements are parked while a question is collapsed.
      Array.prototype.forEach.call(element.querySelectorAll('[required]'), function (control) {
        control.dataset.krumbCakeRequired = 'true';
        control.removeAttribute('required');
      });

      // A question that is no longer on screen must not keep shouting about a
      // missing answer, so its group error is wiped as it goes.
      Array.prototype.forEach.call(
        element.querySelectorAll('[data-krumb-cake-group-error]'),
        function (slot) {
          slot.textContent = '';
        }
      );
      Array.prototype.forEach.call(
        element.querySelectorAll('input[type="radio"]'),
        function (radio) {
          radio.removeAttribute('aria-invalid');
        }
      );

      // Putting a path away must also throw away its answers, or a size chosen
      // on the path the customer abandoned would still be posted.
      if (wasShown && element.hasAttribute('data-krumb-cake-path')) {
        Array.prototype.forEach.call(
          element.querySelectorAll('input[type="radio"], input[type="checkbox"]'),
          function (control) {
            control.checked = false;
          }
        );
        Array.prototype.forEach.call(element.querySelectorAll('select'), function (control) {
          control.value = '';
        });
      }
    }

    reveal(element) {
      if (element.dataset.krumbCakeShown === 'true') return;
      const wasCollapsed = element.dataset.krumbCakeShown === 'false';
      element.dataset.krumbCakeShown = 'true';
      element.hidden = false;

      Array.prototype.forEach.call(
        element.querySelectorAll('[data-krumb-cake-required]'),
        function (control) {
          control.setAttribute('required', 'required');
          delete control.dataset.krumbCakeRequired;
        }
      );

      if (wasCollapsed && element.hasAttribute('data-krumb-cake-step')) {
        element.setAttribute('data-krumb-cake-revealed', 'just-now');
        window.setTimeout(function () {
          element.removeAttribute('data-krumb-cake-revealed');
        }, 500);
      }
    }

    revealEverything() {
      this.gatingActive = false;
      this.pathFilterActive = false;
      const self = this;
      this.controlled.forEach(function (element) {
        self.reveal(element);
      });
      if (this.revealAllButton) this.revealAllButton.hidden = true;
      this.syncBaseFromRadio();
      this.updateTierStack();
      this.applyTierCascade();
      this.updateSummary();
      this.renumberBadges();
      this.updateProgress();
    }

    anyAnswered(names) {
      for (let i = 0; i < names.length; i++) {
        const step = this.querySelector('[data-krumb-cake-step="' + names[i] + '"]');
        if (step && this.isAnswered(step)) return true;
      }
      return false;
    }

    isAnswered(step) {
      const controls = step.querySelectorAll('input, textarea, select');
      for (let i = 0; i < controls.length; i++) {
        const control = controls[i];
        if (control.type === 'hidden' || control.disabled) continue;
        if (control.type === 'radio' || control.type === 'checkbox') {
          if (control.checked) return true;
        } else if (String(control.value).trim() !== '') {
          return true;
        }
      }
      return false;
    }

    visibleSteps() {
      return this.steps.filter(function (step) {
        return !step.hidden;
      });
    }

    // Liquid numbers every step in DOM order, which is right for the no-JS view
    // where both paths are on screen. Once a path is chosen the badges are
    // renumbered so the customer sees a clean 1, 2, 3 down their own path.
    renumberBadges() {
      this.visibleSteps().forEach(function (step, index) {
        const badge = step.querySelector('[data-krumb-cake-badge]');
        if (badge) badge.textContent = String(index + 1);
      });
    }

    updateProgress() {
      if (!this.progress) return;
      const self = this;
      const visible = this.visibleSteps();
      let answered = 0;
      visible.forEach(function (step) {
        if (self.isAnswered(step)) answered += 1;
      });
      // The count is no longer displayed — the client found the cake far more
      // interesting than "4 of 8 answered". It survives as the accessible name
      // of the progressbar, so the hairline fill is labelled rather than
      // decorative, and because this is not a live region it never interrupts.
      const pct = visible.length ? Math.round((answered / visible.length) * 100) : 0;
      const label = this.progressTemplate
        .replace('{current}', String(answered))
        .replace('{total}', String(visible.length));

      this.progress.setAttribute('aria-label', label);
      this.progress.setAttribute('aria-valuenow', String(pct));
      if (this.progressFill) this.progressFill.style.width = pct + '%';
    }

    /* ------------------------------------------------------- tiers + price - */

    tierCount() {
      const chosen = this.querySelector('input[data-krumb-cake-tier-count]:checked');
      if (!chosen) return 1;
      const count = parseInt(chosen.getAttribute('data-krumb-cake-tier-count'), 10);
      return isNaN(count) || count < 1 ? 1 : count;
    }

    // Every tier of the cake the customer asked for must be given a size — there
    // is no "not needed" any more. So a row that is on screen is required, and a
    // row that is not is disabled outright: disabled controls do not submit, do
    // not validate and do not count as answered, which is exactly what an unused
    // tier should do. Hiding alone would leave a required control inside a
    // display:none subtree, which browsers refuse to focus and therefore refuse
    // to report.
    updateTierStack() {
      if (!this.tierRows.length) return;
      const self = this;
      const stackLive = !!this.tiersStep && !this.tiersStep.hidden;
      const chosen = !!this.querySelector('input[data-krumb-cake-tier-count]:checked');
      const count = stackLive ? this.tierCount() : 1;
      // Every tier now carries its own layers and flavour, so even a one-tier
      // cake shows its row - but only once the tier count is actually answered.
      const stackVisible = stackLive && chosen && count >= 1;
      if (this.tierStack) this.tierStack.hidden = !stackVisible;

      // The people path's crowd option: show its stack suggestion while it is
      // the chosen answer, and the first time it is picked, start the customer
      // on two tiers so the suggestion is already coming true below.
      const moreRadio = this.querySelector('input[data-krumb-cake-more-people]');
      const moreHint = this.querySelector('[data-krumb-cake-more-hint]');
      if (moreHint) moreHint.hidden = !(moreRadio && moreRadio.checked);
      if (moreRadio && moreRadio.checked && !chosen && !this.moreAutoTiered) {
        const two = this.querySelector('input[data-krumb-cake-tier-count="2"]');
        if (two) {
          this.moreAutoTiered = true;
          // Deferred: firing the change synchronously would nest a refresh
          // inside this pass, whose stale locals then re-hide the tier rows
          // the nested pass just revealed.
          setTimeout(function () {
            two.checked = true;
            two.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        }
      }

      this.tierRows.forEach(function (row) {
        const tier = parseInt(row.getAttribute('data-krumb-cake-tier-row'), 10);
        const select = row.querySelector('[data-krumb-cake-tier-select]');
        const layersSel = row.querySelector('[data-krumb-cake-tier-layers]');
        const flavourSel = row.querySelector('[data-krumb-cake-tier-flavour]');
        const show = stackVisible && tier <= count;
        row.hidden = !show;
        // A one-tier cake's size is the size question above; its row exists
        // purely for layers + flavour, so the size mirror hides via CSS.
        row.classList.toggle('krumb-cake-tier-row--solo', show && count === 1);

        if (layersSel) layersSel.disabled = !show;
        if (flavourSel) {
          flavourSel.disabled = !show;
          flavourSel.required = show;
          if (show) flavourSel.setAttribute('aria-required', 'true');
          else { flavourSel.removeAttribute('aria-required'); self.setFieldError(flavourSel, ''); }
        }
        if (!select) return;

        if (show && count > 1) {
          select.disabled = false;
          select.required = true;
          select.setAttribute('aria-required', 'true');
        } else {
          select.required = false;
          select.removeAttribute('aria-required');
          delete select.dataset.krumbCakeRequired;
          select.disabled = true;
          self.setFieldError(select, '');
          // Tier 1's select posts nothing (it has no `name`) and is only ever a
          // mirror of the size question, so its value is left alone — blanking
          // it would just fight syncBaseFromRadio() on the next pass.
          if (!select.hasAttribute('data-krumb-cake-tier-base')) select.value = '';
        }
      });
    }

    // A tier can only be the same size as, or smaller than, the tier below it.
    // The ceiling starts at the size chosen in the size question (the bottom
    // tier, whichever path the customer came in by) and tightens as it walks up
    // the stack, so tier 3 is bounded by tier 2's actual answer rather than by
    // the base. Options are filtered rather than validated after the fact: an
    // impossible cake should not be selectable in the first place.
    //
    // There is no no-JS equivalent — the markup offers every size in every
    // select and `tier_stack_help` states the rule in words instead.
    applyTierCascade() {
      if (!this.tierRows.length) return;
      const self = this;
      const base = this.querySelector('input[data-krumb-cake-size]:checked');
      // No base size chosen yet means there is nothing to cascade from, so leave
      // every option available rather than presenting an empty select.
      let limit = base ? this.numberFrom(base, 'inches') : 0;

      this.tierRows.forEach(function (row) {
        if (row.hidden) return;
        const select = row.querySelector('[data-krumb-cake-tier-select]');
        if (!select) return;
        // Tier 1 is the bottom of the stack and therefore the ceiling itself:
        // it is unconstrained, and `limit` already holds its size because it
        // mirrors the size question.
        if (select.hasAttribute('data-krumb-cake-tier-base')) return;

        const chosen = select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;

        Array.prototype.forEach.call(select.options, function (option) {
          if (option.value === '') return; // the "Choose a size..." prompt
          const inches = self.numberFrom(option, 'inches');
          // A size with no `inches` set is allowed through rather than silently
          // vanishing from every tier select. An unconstrained option that Krumb
          // catches by email is a far kinder failure than an option a merchant
          // cannot work out why they can no longer see.
          const allowed = limit <= 0 || inches <= 0 || inches <= limit;
          // `hidden` on an <option> is not honoured everywhere (Safari has been
          // unreliable), so `disabled` is what actually guarantees it cannot be
          // chosen and `hidden` is the nicety on top.
          option.disabled = !allowed;
          option.hidden = !allowed;
        });

        // An upstream change can invalidate what was already chosen here. Clear
        // it and make the customer choose again rather than silently leaving an
        // impossible cake sitting in the form.
        if (chosen && chosen.value !== '' && chosen.disabled) {
          select.value = '';
          self.setFieldError(select, '');
        }

        // Only tighten the ceiling once this tier has a real answer. While it is
        // empty, the tiers above stay bounded by the last size we do know.
        if (select.value !== '') {
          const picked = select.options[select.selectedIndex];
          const inches = picked ? self.numberFrom(picked, 'inches') : 0;
          if (inches > 0) limit = inches;
        }
      });
    }

    // Everything currently contributing to the cake: the base (bottom) tier from
    // the size question, plus every extra tier on screen. Each entry is a DOM
    // node carrying the same data-* attributes, whether it is a radio or an
    // <option>.
    //
    // `complete` is the important half. Now that every visible tier is
    // mandatory, a half-filled stack would total up to less cake and less money
    // than the customer is actually asking for. That is precisely the "total
    // that could be wrong" the estimate must never show, so an incomplete cake
    // reports itself and the estimate stays hidden until the last tier is set.
    selectedParts() {
      const parts = [];
      let complete = true;

      const base = this.querySelector('input[data-krumb-cake-size]:checked');
      if (base) parts.push(base);
      else complete = false;

      this.tierRows.forEach(function (row) {
        if (row.hidden) return;
        const select = row.querySelector('[data-krumb-cake-tier-select]');
        if (!select || select.disabled) return;
        // Tier 1 is already counted above, via the radio it mirrors. Counting
        // the select too would double the bottom tier's servings and price.
        if (select.hasAttribute('data-krumb-cake-tier-base')) return;
        if (!select.value) {
          complete = false;
          return;
        }
        const option = select.options[select.selectedIndex];
        if (option) parts.push(option);
      });

      // Layers and flavour live on the tier rows now: the cake is not fully
      // described until the tier count is answered and every shown tier has a
      // flavour. (Layers always has a value - 2 is preselected.)
      if (this.tierRows.length) {
        const shownRows = this.tierRows.filter(function (row) { return !row.hidden; });
        if (!shownRows.length) complete = false;
        shownRows.forEach(function (row) {
          const f = row.querySelector('[data-krumb-cake-tier-flavour]');
          if (f && !f.disabled && !f.value) complete = false;
        });
      }

      return { parts: parts, complete: complete };
    }

    // The per-tier price additions: layers beyond two cost the size's own
    // 3-/4-layer delta, and the tier's flavour costs its per-size price for
    // THAT tier's size (falling back to the flavour's flat extra).
    tierAdditions(parts) {
      const shownRows = this.tierRows.filter(function (row) { return !row.hidden; });
      const self = this;
      let total = 0;
      parts.forEach(function (part, i) {
        const row = shownRows[i];
        if (!row) return;
        const layersSel = row.querySelector('[data-krumb-cake-tier-layers]');
        const flavourSel = row.querySelector('[data-krumb-cake-tier-flavour]');
        if (layersSel && !layersSel.disabled) {
          const opt = layersSel.selectedOptions[0];
          const n = opt ? parseInt(opt.getAttribute('data-krumb-cake-layers'), 10) : 2;
          if (n === 3) total += self.numberFrom(part, 'l3add');
          else if (n === 4) total += self.numberFrom(part, 'l4add');
        }
        if (flavourSel && !flavourSel.disabled && flavourSel.value) {
          const fopt = flavourSel.selectedOptions[0];
          if (fopt) {
            const flat = self.numberFrom(fopt, 'price-add');
            let bySize = null;
            try { bySize = JSON.parse(fopt.getAttribute('data-krumb-cake-price-by-size') || 'null'); } catch (e) { bySize = null; }
            const label = part.value;
            if (bySize && typeof bySize === 'object' && Object.prototype.hasOwnProperty.call(bySize, label)) {
              const n2 = parseFloat(bySize[label]);
              total += isFinite(n2) && n2 >= 0 ? n2 : (flat > 0 ? flat : 0);
            } else if (flat > 0) {
              total += flat;
            }
          }
        }
      });
      return total;
    }

    numberFrom(node, name) {
      const value = parseFloat(node.getAttribute('data-krumb-cake-' + name));
      return isNaN(value) ? 0 : value;
    }

    /* -------------------------------------------------- the live summary -- */

    setFact(name, value) {
      const fact = this.querySelector('[data-krumb-cake-fact="' + name + '"]');
      if (!fact) return false;
      const slot = fact.querySelector('[data-krumb-cake-fact-value]');
      if (slot) slot.textContent = value || '';
      // An unanswered fact is absent, never a row reading "not chosen yet".
      fact.hidden = !value;
      return !!value;
    }

    // The collection date, in the visitor's own locale. This is the one string
    // in the summary that is not a merchant setting, because it is a date rather
    // than copy — the browser knows how to write it better than we do.
    formatDateForSummary(value) {
      const date = this.parseDate(value);
      if (!date) return '';
      try {
        return date.toLocaleDateString(undefined, {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      } catch (error) {
        return value;
      }
    }

    // Flavours are a multi-select, and the helper text tells people a tiered cake
    // can have a different flavour per tier — so three ticks might mean "one per
    // tier" or "any of these would be lovely". Nothing in the form distinguishes
    // them, so we take the DEAREST selected surcharge rather than adding them
    // up. Summing would invent a number for a cake nobody described.
    //
    // Whether that lands once or once per tier is the merchant's call
    // (flavour_pricing_mode); the default is once, because this figure is
    // labelled "From" and a from-price should be a floor rather than a ceiling.
    //
    // A flavour with no surcharge set reads as 0 here — numberFrom() returns 0
    // for a missing or unparseable attribute. That is deliberate and is NOT the
    // same as the size rule: an unpriced SIZE suppresses the whole figure,
    // because a cake with no base price is unknowable, whereas a flavour with no
    // surcharge is simply a flavour that costs nothing extra. Most of them are.
    extrasTotal() {
      let total = 0;
      this.querySelectorAll('input[data-krumb-cake-extra]').forEach((box) => {
        if (box.closest('[hidden]')) return;
        const qty = this.extraQty(box);
        const price = parseFloat(box.getAttribute('data-price'));
        if (qty > 0 && !isNaN(price)) total += qty * price;
      });
      return total;
    }

    extraQty(box) {
      const n = parseInt(box.value, 10);
      return isNaN(n) ? 0 : Math.max(0, Math.min(12, n));
    }

    setUpPanelDock() {
      // Mobile: the summary is a solid bar across the bottom of the screen,
      // riding the scroll the whole way down. The moment its slot in the form
      // (just before the marketing opt-in) scrolls into view, the bar docks
      // there and becomes an ordinary box in the flow - so it can never sit on
      // top of the send button, the opt-in, or the captcha badge. The docked
      // box is taller than the bar, which acts as natural hysteresis at the
      // boundary. On desktop the panel returns to its sticky left column.
      const bar = this.bar;
      const dock = this.querySelector('[data-krumb-cake-dock]');
      if (!bar || !dock || !window.matchMedia) return;
      const home = bar.parentNode;
      const mq = window.matchMedia('(max-width: 989px)');
      let raf = 0;
      const apply = () => {
        raf = 0;
        if (!mq.matches) {
          bar.classList.remove('krumb-cake-panel--bar', 'krumb-cake-panel--docked');
          if (bar.parentNode !== home) home.appendChild(bar);
          return;
        }
        // The dock slot lives in the gated actions area: while that is still
        // hidden, the bar must ride from its original (always-visible) home -
        // a fixed element inside a hidden ancestor renders as nothing at all.
        const dockVisible = !!dock.offsetParent;
        const barH = bar.offsetHeight || 88;
        const shouldDock = dockVisible && dock.getBoundingClientRect().top <= window.innerHeight - barH - 12;
        if (shouldDock) {
          if (bar.parentNode !== dock) dock.appendChild(bar);
        } else if (bar.parentNode !== home) {
          home.appendChild(bar);
        }
        bar.classList.toggle('krumb-cake-panel--docked', shouldDock);
        bar.classList.toggle('krumb-cake-panel--bar', !shouldDock);
      };
      const kick = () => { if (!raf) raf = requestAnimationFrame(apply); };
      window.addEventListener('scroll', kick, { passive: true });
      window.addEventListener('resize', kick, { passive: true });
      if (mq.addEventListener) mq.addEventListener('change', kick);
      this.addEventListener('change', kick);
      apply();
    }

    /* Shows the in-page thank-you after the enquiry has reached the app.
       This method went missing in a later edit while both callers kept
       calling it, so every successful enquiry threw "showSuccessState is not
       a function": the customer saw nothing happen, and because the throw
       escaped the promise, the fallback form submission never ran either.
       It is also the real conversion point, so the GA4 event belongs here -
       the Liquid success branch only renders on the no-JS fallback path. */
    showSuccessState() {
      const panel = document.querySelector('[data-krumb-cake-success]');
      if (!panel) return false;
      this.form.hidden = true;
      if (this.bar) this.bar.hidden = true;
      const header = document.querySelector('.krumb-cake-intro');
      const heading = header ? header.querySelector('.krumb-cake-heading') : null;
      if (header && heading) {
        const eyebrow = header.querySelector('.krumb-cake-eyebrow');
        const intro = header.querySelector('.krumb-cake-intro-text');
        if (eyebrow && panel.dataset.successEyebrow) eyebrow.textContent = panel.dataset.successEyebrow;
        if (panel.dataset.successTitle) heading.textContent = panel.dataset.successTitle;
        if (intro && panel.dataset.successBlurb) {
          const para = document.createElement('p');
          para.textContent = panel.dataset.successBlurb;
          intro.replaceChildren(para);
        }
        header.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        panel.hidden = false;
        panel.scrollIntoView({ block: 'center' });
        panel.focus();
      }
      // Both submit routes can reach this, so count the enquiry exactly once.
      if (!this.enquiryCounted) {
        this.enquiryCounted = true;
        try {
          if (typeof window.krumbTrack === 'function') window.krumbTrack('cake_enquiry_submit');
        } catch (e) { /* tracking must never break the thank-you */ }
      }
      return true;
    }

    wireExtraSteppers() {
      const self = this;
      this.querySelectorAll('[data-krumb-cake-extra-card]').forEach((card) => {
        const boxes = card.querySelectorAll('input[data-krumb-cake-extra]');
        const paintCard = () => {
          let any = false;
          boxes.forEach((b) => { if (self.extraQty(b) > 0) any = true; });
          card.classList.toggle('krumb-cake-extra-card--on', any);
        };
        card.querySelectorAll('[data-krumb-cake-extra-row]').forEach((row) => {
          const box = row.querySelector('input[data-krumb-cake-extra]');
          if (!box) return;
          const settle = () => {
            const qty = self.extraQty(box);
            box.value = String(qty);
            // A zero quantity carries no field name, so untouched rows never
            // ride the enquiry (or the fallback email) as "Extra: X: 0".
            if (qty > 0) box.name = box.dataset.name;
            else box.removeAttribute('name');
            paintCard();
          };
          row.querySelectorAll('[data-krumb-cake-extra-minus], [data-krumb-cake-extra-plus]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const delta = btn.hasAttribute('data-krumb-cake-extra-plus') ? 1 : -1;
              box.value = String(self.extraQty(box) + delta);
              settle();
              box.dispatchEvent(new Event('change', { bubbles: true }));
            });
          });
          box.addEventListener('input', settle);
          settle();
        });
      });
    }

    updateSummary() {
      if (!this.bar) return;

      const self = this;
      const state = this.selectedParts();
      const parts = state.parts;

      /* ---- the cake itself: shown as it is built up ---------------------- */
      // This one fact is deliberately progressive. It is a list of what has been
      // picked so far, and the tier count travels with it, so a half-filled
      // stack reads honestly as "12 inch / 8 inch - 3 tiers" rather than
      // pretending to be a finished cake. Totals below are held back instead.
      let cake = '';
      if (parts.length) {
        const sizes = [];
        const factRows = this.tierRows.filter(function (row) { return !row.hidden; });
        parts.forEach(function (part, i) {
          if (!part.value) return;
          let bit = part.value;
          const row = factRows[i];
          if (row) {
            const lsel = row.querySelector('[data-krumb-cake-tier-layers]');
            const lopt = lsel && !lsel.disabled ? lsel.selectedOptions[0] : null;
            const n = lopt ? lopt.getAttribute('data-krumb-cake-layers') : null;
            if (n) bit += ' (' + n + ' layers)';
          }
          sizes.push(bit);
        });
        cake = sizes.join(this.summarySeparator);
        const tierRadio = this.querySelector('input[data-krumb-cake-tier-count]:checked');
        if (cake && tierRadio && tierRadio.value) {
          cake = this.summaryCakeTemplate
            .replace('{sizes}', cake)
            .replace('{tiers}', tierRadio.value);
        }
      }
      let showing = this.setFact('cake', cake);

      /* ---- totals: only ever shown for a complete cake ------------------- */
      let minTotal = 0;
      let maxTotal = 0;
      let priceTotal = 0;
      let openEnded = false;
      let everyPartPriced = parts.length > 0;
      let everyPartServes = parts.length > 0;

      parts.forEach(function (part) {
        const min = self.numberFrom(part, 'serves-min');
        const max = self.numberFrom(part, 'serves-max');
        const price = self.numberFrom(part, 'price');
        if (min <= 0) everyPartServes = false;
        minTotal += min;
        if (max <= 0) openEnded = true;
        maxTotal += max;
        if (price <= 0) everyPartPriced = false;
        priceTotal += price;
      });

      // A tier still waiting on a size would under-count both of these, so
      // neither appears until the cake is fully specified.
      const totalsReady = parts.length > 0 && state.complete;

      let serves = '';
      if (totalsReady && everyPartServes && minTotal > 0) {
        serves = openEnded
          ? this.servingsOpenTemplate.replace('{min}', String(minTotal))
          : this.servingsTemplate
              .replace('{min}', String(minTotal))
              .replace('{max}', String(maxTotal));
      }
      if (this.setFact('serves', serves)) showing = true;

      /* ---- collection date ----------------------------------------------- */
      let dateText = '';
      if (this.dateInput && this.dateInput.value && !this.dateProblem()) {
        dateText = this.formatDateForSummary(this.dateInput.value);
      }
      if (this.setFact('date', dateText)) showing = true;

      /* ---- the money ------------------------------------------------------ */
      // Never a total built from a size with no price on it: a suppressed
      // estimate is honest, an under-counted one is not.
      // Note what is NOT in the guard: the surcharge is added inside the branch,
      // so it can only ever ride along with a figure the sizes have already
      // justified. `everyPartPriced` is still computed purely from `parts`, so
      // no flavour can suppress the price and no flavour can conjure one.
      let figure = '';
      if (totalsReady && everyPartPriced && priceTotal > 0) {
        figure = this.pricePrefix + (priceTotal + this.tierAdditions(parts) + this.extrasTotal());
      }

      if (this.barPrice) {
        this.barPrice.textContent = figure
          ? this.summaryPriceTemplate.replace('{price}', figure)
          : '';
        this.barPrice.hidden = !figure;
        if (figure) showing = true;
      }

      // The "starting point, not a quote" framing travels with the figure. It
      // sits in the panel on desktop and beside the send button on phones,
      // where the bar has no room for it; CSS shows exactly one of the two, and
      // neither carries a price, so the figure itself stays single.
      if (this.panelNote) this.panelNote.hidden = !figure;
      if (this.actionsNote) this.actionsNote.hidden = !figure;

      if (this.estimateField) {
        this.estimateField.value = figure;
        this.estimateField.disabled = !figure;
      }

      // The panel never hides. Hiding it left a hole where it belonged and then
      // shunted the whole page sideways the moment the first answer landed, so
      // it holds its ground and swaps its prompt for real facts instead.
      if (this.emptyNote) this.emptyNote.hidden = showing;
    }

    /* --------------------------------------------------------------- date - */

    setUpDateField() {
      if (!this.dateInput) return;
      // The server-rendered `min` can go stale if the page is cached, so the
      // earliest date is recalculated in the browser and whichever of the two is
      // later wins.
      this.dateInput.min = this.formatDate(this.earliestDate());
      this.dateInput.addEventListener(
        'blur',
        function () {
          this.checkDate();
        }.bind(this)
      );
      // Tapping anywhere on the field opens the calendar - not just the tiny
      // icon the browser draws at the far end. showPicker() needs a user
      // gesture and a supporting browser; anywhere it refuses, the click
      // still focuses the field exactly as before.
      this.dateInput.addEventListener(
        'click',
        function () {
          if (typeof this.dateInput.showPicker === 'function') {
            try { this.dateInput.showPicker(); } catch (e) { /* gesture/support refusal */ }
          }
        }.bind(this)
      );
    }

    earliestDate() {
      if (this.cachedMin) return this.cachedMin;

      const fromServer = this.parseDate(this.dataset.minDate);

      const fromClient = new Date();
      fromClient.setHours(0, 0, 0, 0);
      fromClient.setDate(fromClient.getDate() + this.leadDays);

      let earliest = fromServer && fromServer > fromClient ? fromServer : fromClient;

      for (let hop = 0; hop < 14; hop++) {
        if (this.closedDays.indexOf(earliest.getDay()) === -1) break;
        earliest = new Date(earliest.getTime());
        earliest.setDate(earliest.getDate() + 1);
      }

      this.cachedMin = earliest;
      return earliest;
    }

    // Built from parts rather than `new Date(string)` so the value is read in the
    // visitor's own timezone. `new Date('2026-09-12')` is parsed as UTC and lands
    // on the previous day west of Greenwich.
    parseDate(value) {
      if (!value) return null;
      const parts = String(value).split('-');
      if (parts.length !== 3) return null;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      const date = new Date(year, month - 1, day);
      date.setHours(0, 0, 0, 0);
      return date;
    }

    formatDate(date) {
      const month = String(date.getMonth() + 1);
      const day = String(date.getDate());
      return (
        date.getFullYear() +
        '-' +
        (month.length === 1 ? '0' + month : month) +
        '-' +
        (day.length === 1 ? '0' + day : day)
      );
    }

    dateProblem() {
      if (!this.dateInput || !this.dateInput.value) return '';
      const chosen = this.parseDate(this.dateInput.value);
      if (!chosen) return this.messages.required;
      if (this.closedDays.indexOf(chosen.getDay()) !== -1) return this.messages.dateClosed;
      if (chosen < this.earliestDate()) return this.messages.dateSoon;
      return '';
    }

    checkDate() {
      if (!this.dateInput) return '';
      const problem = this.dateProblem();
      this.setFieldError(this.dateInput, problem);
      return problem;
    }

    /* --------------------------------------------------------- validation - */

    setFieldError(control, message) {
      // A radio is never alone: the answer belongs to the whole group, so the
      // error does too. Everything routes through one entry point so callers
      // never have to know which kind of control they are holding.
      if (control.type === 'radio') {
        this.setGroupError(control, message);
        return;
      }
      const field = control.closest('.krumb-cake-field');
      const slot = field ? field.querySelector('[data-krumb-cake-error]') : null;
      if (slot) slot.textContent = message || '';
      if (message) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    }

    // Marks a whole radio group invalid. `aria-invalid` goes on every radio in
    // the group so the state is announced whichever one gets focus, and every
    // radio already points at its own step's slot via aria-describedby, so the
    // message is read out with it.
    //
    // contact[Cake size] deliberately spans two steps (the two paths are one
    // group by name), so each step owns a slot and only the step the customer is
    // actually looking at gets the words — the other is cleared, never left
    // holding a stale copy of the same complaint.
    setGroupError(control, message) {
      const name = control.getAttribute('name');
      const owner = control.closest('[data-krumb-cake-step]');
      const peers = name
        ? this.querySelectorAll('input[name="' + name + '"]')
        : [control];
      const steps = [];

      Array.prototype.forEach.call(peers, function (peer) {
        if (message) peer.setAttribute('aria-invalid', 'true');
        else peer.removeAttribute('aria-invalid');
        const step = peer.closest('[data-krumb-cake-step]');
        if (step && steps.indexOf(step) === -1) steps.push(step);
      });

      steps.forEach(function (step) {
        const slot = step.querySelector('[data-krumb-cake-group-error]');
        if (slot) slot.textContent = message && step === owner ? message : '';
      });
    }

    labelFor(control) {
      let text = '';
      // A radio's own <label> names one option ("Serves 4-6 people, 6 inch..."),
      // which makes a nonsense summary line. The question is what went
      // unanswered, so the group reports under its legend instead.
      if (control.type === 'radio') {
        const step = control.closest('[data-krumb-cake-step]');
        const legend = step ? step.querySelector('.krumb-cake-legend-text') : null;
        if (legend) text = legend.textContent;
      }
      if (!text && control.labels && control.labels.length) text = control.labels[0].textContent;
      if (!text) text = control.name || '';
      return text.replace(/\s+/g, ' ').trim();
    }

    collectProblems() {
      const problems = [];
      // Keys are prefixed so a group called "constructor" could never collide
      // with something inherited from Object.prototype.
      const seenGroups = {};
      const controls = this.querySelectorAll('input, textarea, select');
      for (let i = 0; i < controls.length; i++) {
        const control = controls[i];
        if (control.type === 'hidden' || control.disabled) continue;
        // Collapsed questions are not the customer's problem.
        if (control.closest('[hidden]')) continue;

        // One entry per radio group. contact[Cake size] spans both path
        // questions as a single group by name, so an undeduped pass would list
        // the same missing answer twice and hand the customer two summary links
        // pointing at two different DOM nodes. The hidden skip above has already
        // discarded the collapsed path, so the first survivor of a group is by
        // definition the one the customer can actually see.
        if (control.type === 'radio') {
          const key = 'g:' + (control.getAttribute('name') || '');
          if (seenGroups[key]) continue;
          seenGroups[key] = true;
        }

        this.setFieldError(control, '');
        let message = '';

        if (control.hasAttribute('data-krumb-cake-date')) message = this.dateProblem();

        if (!message) {
          if (control.validity.valueMissing) message = this.messages.required;
          else if (control.type === 'email' && control.validity.typeMismatch) message = this.messages.email;
          else if (control.type === 'url' && control.validity.typeMismatch) message = this.messages.url;
          else if (!control.checkValidity()) message = control.validationMessage;
        }

        if (message) {
          this.setFieldError(control, message);
          problems.push({ control: control, message: message });
        }
      }
      return problems;
    }

    showSummary(problems) {
      if (!this.summary) return;
      const self = this;
      this.summary.textContent = '';

      const heading = document.createElement('p');
      heading.className = 'krumb-cake-summary-heading';
      heading.textContent = this.messages.summaryHeading;

      const list = document.createElement('ul');
      list.className = 'krumb-cake-summary-list';

      problems.forEach(function (problem) {
        const item = document.createElement('li');
        const words = self.labelFor(problem.control) + ' — ' + problem.message;
        if (problem.control.id) {
          const link = document.createElement('a');
          link.href = '#' + problem.control.id;
          link.textContent = words;
          link.addEventListener('click', function (event) {
            event.preventDefault();
            problem.control.focus();
          });
          item.appendChild(link);
        } else {
          item.textContent = words;
        }
        list.appendChild(item);
      });

      this.summary.appendChild(heading);
      this.summary.appendChild(list);
      this.summary.hidden = false;
      this.summary.focus();
    }

    hideSummary() {
      if (!this.summary) return;
      this.summary.hidden = true;
      this.summary.textContent = '';
    }

    /* ------------------------------------------------------------ events -- */

    handleChange(event) {
      const target = event.target;
      // People paste "instagram.com/p/abc" without a scheme and then get told
      // it "does not look like a full web address" - a flash of red nobody
      // deserves. Quietly complete it for them.
      if (target && target.type === 'url' && target.value && !/^https?:\/\//i.test(target.value.trim())) {
        if (/^[\w-]+(\.[\w-]+)+([\/?#]|$)/.test(target.value.trim())) {
          target.value = 'https://' + target.value.trim();
        }
      }
      if (target && target.hasAttribute && target.hasAttribute('data-krumb-cake-tier-base')) {
        // Editing the bottom tier in the stack is editing the answer to the size
        // question. Push it there first, then let the single refresh below
        // re-run the cascade against the new ceiling.
        this.syncRadioFromBase();
      }
      if (target && target.hasAttribute && target.hasAttribute('data-krumb-cake-date')) {
        this.checkDate();
      } else if (target && target.getAttribute && target.getAttribute('aria-invalid') === 'true') {
        // Clear a field's error as soon as the customer engages with it again.
        if (target.checkValidity()) this.setFieldError(target, '');
      }
      this.refresh();
    }

    submitViaBridge() {
      const stillClosed = this.controlled.some(function (element) { return element.hidden; });
      if ((this.gatingActive || this.pathFilterActive) && stillClosed) {
        this.revealEverything();
      }
      let problems;
      try { problems = this.collectProblems(); } catch (error) { this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit(); return; }
      if (problems.length) { this.showSummary(problems); return; }
      this.hideSummary();
      const fallback = () => {
        this.bridgeSettled = true;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
      };
      const succeed = () => { if (!this.showSuccessState()) fallback(); };
      const attempt = this.sendDraftBridge();
      if (!attempt) { fallback(); return; }
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; fallback(); } }, 6000);
      attempt.then((r) => {
        if (done) return;
        done = true; clearTimeout(timer);
        if (r && r.ok) succeed(); else fallback();
      }, () => { if (!done) { done = true; clearTimeout(timer); fallback(); } });
    }

    handleSubmit(event) {
      // A Shopify Forms app block rendered in the reference-photo question sits
      // inside our <form>, because HTML has no way for it not to. If its own
      // <form> is ever flattened by the parser, its button becomes a submit
      // button for the cake enquiry. Only our own button may submit this form.
      if (event.submitter && !event.submitter.classList.contains('krumb-cake-submit')) {
        event.preventDefault();
        return;
      }

      // Pressing Enter inside a radio group can submit a form whose submit button
      // is still collapsed. Open everything first, then judge it.
      const stillClosed = this.controlled.some(function (element) {
        return element.hidden;
      });
      if ((this.gatingActive || this.pathFilterActive) && stillClosed) {
        event.preventDefault();
        this.revealEverything();
        const remaining = this.collectProblems();
        if (remaining.length) {
          this.showSummary(remaining);
        } else if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.submit();
        }
        return;
      }

      let problems;
      try {
        problems = this.collectProblems();
      } catch (error) {
        // Never let a scripting fault swallow a real enquiry: hand validation
        // straight back to the browser and let it decide.
        this.form.noValidate = false;
        if (!this.form.checkValidity()) {
          event.preventDefault();
          this.form.reportValidity();
        }
        return;
      }

      if (problems.length === 0) {
        this.hideSummary();
        // Hold the submission for up to ~a second so the draft-order call is
        // guaranteed to leave the browser before any navigation, then re-fire
        // the submit with the bridge marked done so every later handler
        // (Shopify's captcha included) runs exactly as normal.
        if (!this.bridgeSettled) {
          event.preventDefault();
          // Keep this submit from reaching Shopify's captcha bootstrap at the
          // document level - on the bridge path there is nothing to protect.
          // The fallback's requestSubmit() raises a fresh event that
          // propagates normally, so the captcha still guards that path.
          event.stopPropagation();
          // The bridge IS the submission now: on success the customer sees the
          // thank-you panel and never meets a captcha. Only if the bridge
          // fails or times out do we fall back to the classic contact-form
          // submission (Shopify's captcha and email), so no enquiry is lost.
          const fallback = () => {
            this.bridgeSettled = true;
            if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
            else this.form.submit();
          };
          const succeed = () => { if (!this.showSuccessState()) fallback(); };
          const attempt = this.sendDraftBridge();
          if (!attempt) { fallback(); return; }
          let done = false;
          const timer = setTimeout(() => { if (!done) { done = true; fallback(); } }, 6000);
          attempt.then((r) => {
            if (done) return;
            done = true; clearTimeout(timer);
            if (r && r.ok) succeed(); else fallback();
          }, () => { if (!done) { done = true; clearTimeout(timer); fallback(); } });
        }
        return;
      }

      event.preventDefault();
      this.showSummary(problems);
    }

    sendDraftBridge() {
      // Fire-and-forget copy of the enquiry to the draft-order bridge, sent
      // as the real form submission proceeds. The contact email stays the
      // submission of record: if this request fails, nothing changes for the
      // customer or the inbox. text/plain avoids a CORS preflight, and
      // keepalive lets the request outlive the page navigation.
      const endpoint = this.dataset.enquiryEndpoint;
      if (!endpoint || !window.fetch) return;
      try {
        const data = new FormData(this.form);
        const choices = [];
        let email = '';
        data.forEach((value, key) => {
          const m = key.match(/^contact\[(.+)\]$/);
          if (!m || value instanceof File || String(value).trim() === '') return;
          if (m[1].toLowerCase() === 'email') { email = String(value); return; }
          choices.push({ label: m[1], value: String(value).slice(0, 250) });
        });
        const estimate = parseFloat(
          String((this.estimateField && this.estimateField.value) || '').replace(/[^0-9.]/g, '')
        );
        const size = String(data.get('contact[Cake size]') || '');
        const extras = [...this.querySelectorAll('input[data-krumb-cake-extra]')]
          .filter((box) => !box.closest('[hidden]') && this.extraQty(box) > 0)
          .map((box) => ({
            variantId: box.getAttribute('data-variant-id'),
            price: box.getAttribute('data-price'),
            quantity: this.extraQty(box),
          }));
        const payload = JSON.stringify({ email, estimate, size, choices, extras });
        return fetch(endpoint, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: payload,
        }).then((resp) => ({ ok: resp.ok })).catch(() => {
          // Second delivery mechanism for browsers/extensions that kill
          // fetch: a beacon survives navigation and same-origin text/plain
          // needs no preflight.
          try { navigator.sendBeacon(endpoint, new Blob([payload], { type: 'text/plain' })); } catch (e2) {}
        });
      } catch (error) {
        /* deliberately silent - the email path must never be disturbed */
      }
      return null;
    }
  }

  customElements.define('krumb-cake-enquiry', KrumbCakeEnquiry);
}
